import { useEffect, useMemo, useState } from 'react';
import {
  collection, onSnapshot, addDoc, updateDoc, deleteDoc, doc,
  serverTimestamp, query, orderBy, getDocs, limit,
} from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import { PLAYING_LEVELS, ACCOMMODATION_GROUPS, roomLabel, levelLabel } from '../../constants/padel';
import { LEAD_STATUSES, SalesFilterBar, useSalesFilters, recordOrigin } from './salesFilters';

// ─── Shared constants ─────────────────────────────────────────────────────────

// Re-exported so the Spanish database keeps importing its statuses from here.
export { LEAD_STATUSES };

const STATUS_COLOURS = {
  'New':            'bg-blue-100 text-blue-700',
  'Contacted':      'bg-yellow-100 text-yellow-700',
  'Follow-up':      'bg-orange-100 text-orange-700',
  'Quoted':         'bg-purple-100 text-purple-700',
  'Won':            'bg-green-100 text-green-700',
  'Not interested': 'bg-gray-100 text-gray-500',
};

const SOURCES = ['Instagram', 'Facebook', 'TikTok', 'QR code', 'Website', 'Organic', 'Other'];

function getUserRoles(u) {
  if (Array.isArray(u.roles)) return u.roles;
  if (u.role) return [u.role];
  return [];
}

// Scrape batch ids are "<city>-YYYY-MM" (see scripts/importClubLeads.js) — pull
// the city back out for a coarse geography filter. Hand-added/client leads have
// no importBatch and so have no city.
function batchCity(importBatch) {
  const m = /^([a-z]+)-\d{4}-\d{2}$/.exec(importBatch || '');
  return m ? m[1][0].toUpperCase() + m[1].slice(1) : '';
}

// ─── Notes log helpers (shared with the Spanish Sales tab) ────────────────────
// A note entry is { text, authorName, authorUid, at (ms), system? }. We use a
// client-side millisecond timestamp because Firestore's serverTimestamp() is
// not allowed inside array elements.

export function makeNote(text, currentUser, system = false) {
  return {
    text,
    authorName: currentUser?.displayName || currentUser?.email || '',
    authorUid: currentUser?.uid || '',
    at: Date.now(),
    ...(system ? { system: true } : {}),
  };
}

// Normalises whatever a record holds into a newest-first array of entries,
// folding a legacy single-string notes field in as the oldest entry so nothing
// is ever lost during the migration.
export function normalizeLog(log, legacyNotes) {
  if (Array.isArray(log) && log.length) return log;
  if (legacyNotes) return [{ text: legacyNotes, authorName: '', at: null }];
  return [];
}

function fmtWhen(at) {
  if (!at) return '';
  const d = at.toDate ? at.toDate() : new Date(at);
  if (isNaN(d)) return '';
  return d.toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export function NotesLog({ entries }) {
  if (!entries.length) {
    return <p className="text-[12px] text-[#9CA3AF]">No notes yet.</p>;
  }
  return (
    <div className="space-y-2 max-h-56 overflow-y-auto">
      {entries.map((n, i) => (
        <div key={i} className={`text-[12px] rounded-[8px] px-3 py-2 ${n.system ? 'bg-[#F3F4F6] text-[#6B7280] italic' : 'bg-[#F9FAFB] text-[#374151]'}`}>
          <p className="whitespace-pre-wrap">{n.text}</p>
          {(n.authorName || n.at) && (
            <p className="text-[10px] text-[#9CA3AF] mt-1">{[n.authorName, fmtWhen(n.at)].filter(Boolean).join(' · ')}</p>
          )}
        </div>
      ))}
    </div>
  );
}

// Add-note composer reused by both databases.
export function AddNote({ onAdd }) {
  const [text, setText] = useState('');
  function submit() {
    const t = text.trim();
    if (!t) return;
    onAdd(t);
    setText('');
  }
  return (
    <div className="flex gap-2 items-end">
      <textarea
        value={text} onChange={(e) => setText(e.target.value)} rows={2}
        placeholder="Add a note about your last contact…"
        className="flex-1 border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-[#1A4A8A]"
      />
      <button
        onClick={submit} disabled={!text.trim()}
        className="px-3 py-2 bg-[#1A4A8A] text-white text-[13px] font-semibold rounded-[8px] hover:bg-[#0D2240] transition disabled:opacity-40 flex-shrink-0"
      >
        Add note
      </button>
    </div>
  );
}

// ─── Small shared UI (mirrors TeamDatabasePage styling) ───────────────────────

function Label({ children, required }) {
  return (
    <label className="block text-[13px] font-medium text-[#374151] mb-1">
      {children}{required && <span className="text-red-500 ml-0.5">*</span>}
    </label>
  );
}

function Input({ value, onChange, placeholder, type = 'text' }) {
  return (
    <input type={type} value={value} onChange={onChange} placeholder={placeholder}
      className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-[#1A4A8A]"
    />
  );
}

function Sel({ value, onChange, children }) {
  return (
    <select value={value} onChange={onChange}
      className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-[#1A4A8A] bg-white"
    >
      {children}
    </select>
  );
}

// Add-and-chip input for a team's year(s) of birth. Values are kept as strings,
// sorted ascending, so age groups filter and display consistently.
function YearsOfBirthInput({ values, onChange }) {
  const [inputVal, setInputVal] = useState('');

  function addYear() {
    const y = inputVal.trim();
    const n = Number(y);
    if (!y || isNaN(n) || n < 1990 || n > 2025) return;
    if (values.includes(y)) { setInputVal(''); return; }
    onChange([...values, y].sort((a, b) => a - b));
    setInputVal('');
  }

  return (
    <div>
      <div className="flex gap-2 mb-2">
        <input
          type="number" min="1990" max="2025" value={inputVal}
          onChange={(e) => setInputVal(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addYear())}
          placeholder="e.g. 2010"
          className="flex-1 border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-[#1A4A8A]"
        />
        <button type="button" onClick={addYear}
          className="px-3 py-2 bg-[#1A4A8A] text-white text-[13px] font-semibold rounded-[8px] hover:bg-[#0D2240] transition">
          Add
        </button>
      </div>
      {values.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {values.map((y) => (
            <span key={y} className="inline-flex items-center gap-1 px-2.5 py-1 bg-[#EFF6FF] border border-[#93C5FD] rounded-full text-[13px] font-semibold text-[#1A4A8A]">
              {y}
              <button type="button" onClick={() => onChange(values.filter((v) => v !== y))} className="text-[#93C5FD] hover:text-[#1A4A8A] transition leading-none">×</button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function Modal({ onClose, title, badge, children }) {
  return (
    <div className="fixed inset-0 z-[9999] flex items-start justify-center bg-black/40 overflow-y-auto py-8 px-4">
      <div className="bg-white rounded-[14px] shadow-xl w-full max-w-lg my-auto">
        <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-[#E5E7EB]">
          <div className="flex items-center gap-2 min-w-0">
            <p className="text-[15px] font-bold text-[#111827] truncate">{title}</p>
            {badge}
          </div>
          <button onClick={onClose} className="text-[#9CA3AF] hover:text-[#374151] transition text-xl leading-none flex-shrink-0">×</button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  return (
    <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${STATUS_COLOURS[status] || 'bg-gray-100 text-gray-600'}`}>
      {status || 'New'}
    </span>
  );
}

function fmtDate(ts) {
  if (!ts) return null;
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  if (isNaN(d)) return null;
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

const EMPTY = {
  contactName: '', clubSchool: '', email: '', phone: '', address: '',
  playingLevel: '', partySize: '', accommodation: '',
  status: 'New', source: '', assignedTo: '', initialNote: '', yearsOfBirth: [],
};

// ─── Add / edit lead form modal ───────────────────────────────────────────────

function LeadModal({ lead, staff, showSource, padel = false, onClose, onSave }) {
  const [form, setForm] = useState(lead ? { ...EMPTY, ...lead } : EMPTY);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const isNew = !lead;

  const valid = form.clubSchool.trim() || form.contactName.trim();

  async function handleSave() {
    if (!valid || saving) return;
    setSaving(true);
    const assignee = staff.find((s) => s.id === form.assignedTo);
    await onSave({
      contactName: form.contactName.trim(),
      clubSchool: form.clubSchool.trim(),
      email: form.email.trim(),
      phone: form.phone.trim(),
      address: form.address.trim(),
      yearsOfBirth: Array.isArray(form.yearsOfBirth) ? form.yearsOfBirth : [],
      ...(padel ? {
        playingLevel: form.playingLevel || '',
        partySize: Math.max(0, Math.round(Number(form.partySize) || 0)),
        accommodation: form.accommodation || '',
      } : {}),
      status: form.status || 'New',
      ...(showSource ? { source: form.source || '' } : {}),
      assignedTo: form.assignedTo || '',
      assignedToName: assignee ? (assignee.name || assignee.email || '') : '',
      ...(isNew ? { initialNote: form.initialNote.trim() } : {}),
    });
  }

  return (
    <Modal onClose={onClose} title={isNew ? 'Add lead' : 'Edit details'}>
      <div className="space-y-4">
        <div>
          <Label required>{padel ? 'Club they most often play at' : 'Club / school'}</Label>
          <Input value={form.clubSchool} onChange={set('clubSchool')} placeholder={padel ? 'Home club' : 'Organisation name'} />
        </div>
        <div>
          <Label>{padel ? 'Name' : 'Contact person'}</Label>
          <Input value={form.contactName} onChange={set('contactName')} placeholder="Full name" />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Email</Label>
            <Input type="email" value={form.email} onChange={set('email')} placeholder="name@email.com" />
          </div>
          <div>
            <Label>Phone</Label>
            <Input value={form.phone} onChange={set('phone')} placeholder="Phone number" />
          </div>
        </div>
        {padel ? (
          <>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Playing level</Label>
                <Sel value={form.playingLevel} onChange={set('playingLevel')}>
                  <option value="">Not set</option>
                  {PLAYING_LEVELS.map(([level, desc]) => (
                    <option key={level} value={level}>{level} — {desc}</option>
                  ))}
                </Sel>
              </div>
              <div>
                <Label>Travellers in party</Label>
                <Input type="number" value={form.partySize} onChange={set('partySize')} placeholder="e.g. 8" />
              </div>
            </div>
            <div>
              <Label>Accommodation preference</Label>
              <Sel value={form.accommodation} onChange={set('accommodation')}>
                <option value="">Not set</option>
                {ACCOMMODATION_GROUPS.map(([hotel, options]) => (
                  <optgroup key={hotel} label={hotel}>
                    {options.map((o) => <option key={o} value={o}>{roomLabel(o)}</option>)}
                  </optgroup>
                ))}
              </Sel>
            </div>
          </>
        ) : (
          <>
            <div>
              <Label>Address</Label>
              <Input value={form.address} onChange={set('address')} placeholder="Town / address" />
            </div>
            <div>
              <Label>Year(s) of birth</Label>
              <YearsOfBirthInput
                values={Array.isArray(form.yearsOfBirth) ? form.yearsOfBirth : []}
                onChange={(years) => setForm((f) => ({ ...f, yearsOfBirth: years }))}
              />
              <p className="text-[11px] text-[#9CA3AF] mt-1">Add each squad's birth year so leads can be grouped by age for tournaments.</p>
            </div>
          </>
        )}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Status</Label>
            <Sel value={form.status} onChange={set('status')}>
              {LEAD_STATUSES.map((s) => <option key={s}>{s}</option>)}
            </Sel>
          </div>
          {showSource ? (
            <div>
              <Label>Source</Label>
              <Sel value={form.source} onChange={set('source')}>
                <option value="">Not set</option>
                {SOURCES.map((s) => <option key={s}>{s}</option>)}
              </Sel>
            </div>
          ) : (
            <div>
              <Label>Assigned to</Label>
              <Sel value={form.assignedTo} onChange={set('assignedTo')}>
                <option value="">Unassigned</option>
                {staff.map((s) => <option key={s.id} value={s.id}>{s.name || s.email}</option>)}
              </Sel>
            </div>
          )}
        </div>
        {showSource && (
          <div>
            <Label>Assigned to</Label>
            <Sel value={form.assignedTo} onChange={set('assignedTo')}>
              <option value="">Unassigned</option>
              {staff.map((s) => <option key={s.id} value={s.id}>{s.name || s.email}</option>)}
            </Sel>
          </div>
        )}
        {isNew && (
          <div>
            <Label>First note (optional)</Label>
            <textarea
              value={form.initialNote} onChange={set('initialNote')} rows={3}
              placeholder="Where this lead came from, first impressions…"
              className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-[#1A4A8A]"
            />
          </div>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="px-4 py-2 text-[13px] text-[#6B7280] hover:text-[#374151] font-medium">Cancel</button>
          <button
            onClick={handleSave} disabled={!valid || saving}
            className="px-5 py-2.5 bg-[#1A4A8A] text-white text-[13px] font-semibold rounded-[8px] hover:bg-[#0D2240] transition disabled:opacity-40"
          >
            {saving ? 'Saving…' : isNew ? 'Add lead' : 'Save changes'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ─── Lead detail modal (opened by clicking a card) ────────────────────────────

function LeadDetailModal({ lead, staff, currentUser, showSource, padel = false, canManage = true, enableCrm = false, onPatch, onEdit, onDelete, onCopyToCrm, onViewInCrm, onClose }) {
  const entries = normalizeLog(lead.noteLog, lead.notes);
  const lastContacted = fmtDate(lead.lastContactedAt);

  // Every mutation folds the existing log forward and clears the legacy notes
  // string, so the log becomes the single source of truth on first write.
  function withNote(text, system, extra = {}) {
    const existing = normalizeLog(lead.noteLog, lead.notes);
    return { ...extra, noteLog: [makeNote(text, currentUser, system), ...existing], notes: '' };
  }

  function addNote(text)   { onPatch(withNote(text, false)); }
  function changeStatus(s) {
    if (s === (lead.status || 'New')) return;
    onPatch(withNote(`Status changed to ${s}`, true, { status: s }));
  }
  function changeAssignee(uid) {
    const a = staff.find((x) => x.id === uid);
    const name = a ? (a.name || a.email || '') : '';
    onPatch(withNote(uid ? `Assigned to ${name}` : 'Unassigned', true, { assignedTo: uid, assignedToName: name }));
  }
  function markContacted() {
    const s = (lead.status === 'New' || !lead.status) ? 'Contacted' : lead.status;
    onPatch(withNote('Marked as contacted', true, { lastContactedAt: serverTimestamp(), status: s }));
  }

  return (
    <Modal onClose={onClose} title={lead.clubSchool || lead.contactName || 'Lead'} badge={<StatusBadge status={lead.status} />}>
      <div className="space-y-4">
        {/* Contact details */}
        <div className="text-[13px] space-y-2">
          {lead.origin === 'portal-customer' && (
            <p className="text-[#065F46] font-semibold">✓ Returning client{lead.tourName ? ` · ${lead.tourName}` : ''}</p>
          )}
          {enableCrm && lead.inCrm && (
            <p className="text-[#1A4A8A] font-semibold">
              ✓ In CRM
              {onViewInCrm && (
                <button onClick={() => onViewInCrm(lead)} className="ml-2 text-[11px] underline hover:no-underline">Open tour →</button>
              )}
            </p>
          )}
          <div>
            <p className="text-[#9CA3AF] text-[11px] font-medium uppercase tracking-wide">
              {padel ? 'Club they most often play at' : 'Club / school'}
            </p>
            <p className="text-[#111827] font-semibold">{lead.clubSchool || '—'}</p>
          </div>
          {lead.contactName && (
            <div>
              <p className="text-[#9CA3AF] text-[11px] font-medium uppercase tracking-wide">{padel ? 'Name' : 'Contact person'}</p>
              <p className="text-[#111827]">{lead.contactName}</p>
            </div>
          )}
          {padel && (
            <>
              <div>
                <p className="text-[#9CA3AF] text-[11px] font-medium uppercase tracking-wide">Playing level</p>
                <p className="text-[#111827]">{levelLabel(lead.playingLevel) || '—'}</p>
              </div>
              <div>
                <p className="text-[#9CA3AF] text-[11px] font-medium uppercase tracking-wide">Expected travellers in party</p>
                <p className="text-[#111827]">{lead.partySize || '—'}</p>
              </div>
              <div>
                <p className="text-[#9CA3AF] text-[11px] font-medium uppercase tracking-wide">Accommodation preference</p>
                <p className="text-[#111827]">{lead.accommodation || '—'}</p>
              </div>
            </>
          )}
          {Array.isArray(lead.yearsOfBirth) && lead.yearsOfBirth.length > 0 && (
            <div>
              <p className="text-[#9CA3AF] text-[11px] font-medium uppercase tracking-wide">Year(s) of birth</p>
              <div className="flex flex-wrap gap-1.5 mt-1">
                {lead.yearsOfBirth.map((y) => (
                  <span key={y} className="px-2 py-0.5 bg-[#EFF6FF] border border-[#93C5FD] rounded-full text-[12px] font-semibold text-[#1A4A8A]">{y}</span>
                ))}
              </div>
            </div>
          )}
          <div className="space-y-0.5 pt-1">
            {lead.email && <p className="text-[#6B7280]">{lead.email}</p>}
            {lead.phone && <p className="text-[#6B7280]">{lead.phone}</p>}
            {lead.address && <p className="text-[#9CA3AF]">{lead.address}</p>}
            {showSource && lead.source && <p className="text-[#9CA3AF]">Source: <span className="font-medium text-[#6B7280]">{lead.source}</span></p>}
            {lastContacted && <p className="text-[#9CA3AF]">Last contacted: {lastContacted}</p>}
          </div>
        </div>

        {/* Status + assignee (inline, auto-saves + logs) */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Status</Label>
            <Sel value={lead.status || 'New'} onChange={(e) => changeStatus(e.target.value)}>
              {LEAD_STATUSES.map((s) => <option key={s}>{s}</option>)}
            </Sel>
          </div>
          <div>
            <Label>Assigned to</Label>
            <Sel value={lead.assignedTo || ''} onChange={(e) => changeAssignee(e.target.value)}>
              <option value="">Unassigned</option>
              {staff.map((s) => <option key={s.id} value={s.id}>{s.name || s.email}</option>)}
            </Sel>
          </div>
        </div>

        {/* Notes log */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <Label>Notes &amp; activity</Label>
            <button onClick={markContacted} className="text-[12px] text-[#065F46] hover:underline font-semibold">Mark contacted today</button>
          </div>
          <div className="mb-3"><AddNote onAdd={addNote} /></div>
          <NotesLog entries={entries} />
        </div>

        {/* Footer actions */}
        <div className="flex items-center justify-between pt-2 border-t border-[#E5E7EB]">
          {canManage
            ? <button onClick={onDelete} className="text-[13px] text-[#DC2626] hover:underline font-medium">Delete lead</button>
            : <span />}
          <div className="flex gap-2">
            {enableCrm && canManage && !lead.inCrm && (() => {
              const missing = !lead.contactName?.trim() || !lead.email?.trim();
              return (
                <button
                  onClick={() => !missing && onCopyToCrm(lead)}
                  disabled={missing}
                  title={missing ? 'Add a contact name and email before copying to CRM — needed for the automated emails.' : undefined}
                  className="px-4 py-2 bg-white border border-[#1A4A8A] text-[13px] font-semibold text-[#1A4A8A] rounded-[8px] hover:bg-[#EFF6FF] transition disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-white"
                >
                  Copy to CRM
                </button>
              );
            })()}
            {canManage && (
              <button onClick={onEdit} className="px-4 py-2 bg-white border border-[#E5E7EB] text-[13px] font-semibold text-[#374151] rounded-[8px] hover:bg-[#F9FAFB] transition">Edit details</button>
            )}
            <button onClick={onClose} className="px-4 py-2 bg-[#1A4A8A] text-white text-[13px] font-semibold rounded-[8px] hover:bg-[#0D2240] transition">Done</button>
          </div>
        </div>
      </div>
    </Modal>
  );
}

// ─── Main lead-database view ──────────────────────────────────────────────────

export default function LeadDatabase({ collectionName, subtitle, showSource = false, padel = false, canManage = true, enableCrm = false, onCopyToCrm, onViewInCrm }) {
  const { currentUser } = useAuth();
  const [leads, setLeads]   = useState([]);
  const [staff, setStaff]   = useState([]);
  const [loading, setLoading] = useState(true);

  const f = useSalesFilters(currentUser?.uid);
  const [sourceFilter, setSourceFilter] = useState(''); // marketing channel chips (padel)
  const [cityFilter, setCityFilter] = useState(''); // scrape-batch city, e.g. "Manchester"
  const [yobFilter, setYobFilter] = useState([]); // selected years of birth (OR match)

  const [editing, setEditing]       = useState(null); // lead object or {} for new
  const [selectedId, setSelectedId] = useState(null); // open detail modal
  const [toast, setToast]           = useState('');

  function showToast(msg) { setToast(msg); setTimeout(() => setToast(''), 3000); }

  useEffect(() => {
    setLoading(true);
    const unsub = onSnapshot(
      query(collection(db, collectionName), orderBy('createdAt', 'desc')),
      (snap) => { setLeads(snap.docs.map((d) => ({ id: d.id, ...d.data() }))); setLoading(false); },
      () => setLoading(false),
    );
    return unsub;
  }, [collectionName]);

  useEffect(() => {
    getDocs(query(collection(db, 'users'), limit(500))).then((snap) => {
      const list = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((u) => getUserRoles(u).some((r) => r === 'staff' || r === 'admin'))
        .sort((a, b) => (a.name || a.email || '').localeCompare(b.name || b.email || ''));
      setStaff(list);
    }).catch(() => {});
  }, []);

  const selected = leads.find((l) => l.id === selectedId) || null;

  // Bulk imports dwarf the leads staff actually know about, so say how many the
  // default view is holding back rather than letting the totals look wrong.
  const hiddenScraped = f.includeScraped
    ? 0
    : leads.filter((l) => recordOrigin(l) === 'scraped').length;

  // Breakdown of where leads came from (newest-first by count), for the summary bar.
  const sourceCounts = useMemo(() => {
    const m = {};
    leads.forEach((l) => { const s = l.source || 'Unknown'; m[s] = (m[s] || 0) + 1; });
    return Object.entries(m).sort((a, b) => b[1] - a[1]).map(([source, count]) => ({ source, count }));
  }, [leads]);

  // Scrape-batch cities present in this collection, most leads first, for the filter.
  const cityOptions = useMemo(() => {
    const counts = {};
    leads.forEach((l) => { const c = batchCity(l.importBatch); if (c) counts[c] = (counts[c] || 0) + 1; });
    return Object.entries(counts).sort((a, b) => b[1] - a[1]);
  }, [leads]);

  // All distinct years of birth across leads, newest-age-group first, for the filter.
  const yobOptions = useMemo(() => {
    const set = new Set();
    leads.forEach((l) => (Array.isArray(l.yearsOfBirth) ? l.yearsOfBirth : []).forEach((y) => set.add(String(y))));
    return Array.from(set).sort((a, b) => Number(b) - Number(a));
  }, [leads]);

  const filtered = leads.filter((l) => {
    if (sourceFilter && (l.source || 'Unknown') !== sourceFilter) return false;
    if (cityFilter && batchCity(l.importBatch) !== cityFilter) return false;
    if (yobFilter.length) {
      const years = Array.isArray(l.yearsOfBirth) ? l.yearsOfBirth.map(String) : [];
      if (!yobFilter.some((y) => years.includes(y))) return false;
    }
    const noteText = (Array.isArray(l.noteLog) ? l.noteLog.map((n) => n.text).join(' ') : '') + ' ' + (l.notes || '');
    return f.matches({
      status: l.status || 'New',
      assignedTo: l.assignedTo,
      origin: recordOrigin(l),
      entityType: l.entityType || '',
      hasContact: Boolean(l.email || l.phone),
      marketingConsent: l.marketingConsent,
      haystack: [l.clubSchool, l.contactName, l.email, l.phone, l.address,
                 l.playingLevel, l.accommodation, noteText].filter(Boolean).join(' '),
    });
  });

  function patchLead(id, data) {
    return updateDoc(doc(db, collectionName, id), { ...data, updatedAt: serverTimestamp() });
  }

  async function handleSave(data) {
    const { initialNote, ...rest } = data;
    if (editing?.id) {
      await patchLead(editing.id, rest);
      showToast('Lead updated.');
    } else {
      await addDoc(collection(db, collectionName), {
        ...rest,
        noteLog: initialNote ? [makeNote(initialNote, currentUser)] : [],
        createdAt: serverTimestamp(),
        createdBy: currentUser?.email || '',
        lastContactedAt: null,
      });
      showToast('Lead added.');
    }
    setEditing(null);
  }

  async function handleDelete(lead) {
    const msg = lead.inCrm
      ? 'Delete this lead? This cannot be undone, and its linked CRM record will lose its reference back to it.'
      : 'Delete this lead? This cannot be undone.';
    if (!window.confirm(msg)) return;
    await deleteDoc(doc(db, collectionName, lead.id));
    if (selectedId === lead.id) setSelectedId(null);
    showToast('Lead deleted.');
  }

  async function markContacted(lead) {
    const existing = normalizeLog(lead.noteLog, lead.notes);
    const s = (lead.status === 'New' || !lead.status) ? 'Contacted' : lead.status;
    await patchLead(lead.id, {
      lastContactedAt: serverTimestamp(),
      status: s,
      noteLog: [makeNote('Marked as contacted', currentUser, true), ...existing],
      notes: '',
    });
    showToast('Marked as contacted.');
  }

  return (
    <div className="space-y-5">
      {toast && (
        <div className="fixed top-5 right-5 z-[99999] bg-[#065F46] text-white px-4 py-3 rounded-[10px] shadow-lg text-[13px] font-semibold">
          {toast}
        </div>
      )}

      {/* Header row */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <p className="text-[13px] text-[#6B7280]">
          {subtitle} — {leads.length} lead(s){filtered.length !== leads.length ? ` · ${filtered.length} shown` : ''}
          {hiddenScraped > 0 && <span className="text-[#9CA3AF]"> · {hiddenScraped} scraped hidden</span>}
        </p>
        {canManage && (
          <button
            onClick={() => setEditing({})}
            className="px-4 py-2 bg-[#1A4A8A] text-white text-[13px] font-semibold rounded-[8px] hover:bg-[#0D2240] transition flex items-center gap-1.5 self-start sm:self-auto"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" /></svg>
            Add lead
          </button>
        )}
      </div>

      {/* Filters — identical to the Spanish database's Sales tab */}
      <SalesFilterBar f={f} staff={staff} showEntityType={!padel} showScraped={!padel} />

      {/* Where leads came from */}
      {showSource && leads.length > 0 && (
        <div>
          <p className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-[0.9px] mb-2">Where leads came from</p>
          <div className="flex flex-wrap gap-2">
            <button
              type="button" onClick={() => setSourceFilter('')}
              className={`px-3 py-1.5 rounded-[8px] border text-[12px] font-medium transition ${
                !sourceFilter ? 'bg-[#1A4A8A] border-[#1A4A8A] text-white' : 'bg-white border-[#E5E7EB] text-[#374151] hover:border-[#93C5FD]'
              }`}
            >
              All <span className={!sourceFilter ? 'text-white/70' : 'text-[#9CA3AF]'}>· {leads.length}</span>
            </button>
            {sourceCounts.map(({ source, count }) => {
              const active = sourceFilter === source;
              return (
                <button
                  key={source} type="button"
                  onClick={() => setSourceFilter(active ? '' : source)}
                  className={`px-3 py-1.5 rounded-[8px] border text-[12px] font-medium transition ${
                    active ? 'bg-[#1A4A8A] border-[#1A4A8A] text-white' : 'bg-white border-[#E5E7EB] text-[#374151] hover:border-[#93C5FD]'
                  }`}
                >
                  {source} <span className={active ? 'text-white/70' : 'text-[#9CA3AF]'}>· {count}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Filter by scrape-batch city — e.g. Manchester vs Glasgow */}
      {cityOptions.length > 1 && (
        <div>
          <div className="flex items-center gap-3 mb-2">
            <p className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-[0.9px]">City</p>
            {cityFilter && (
              <button type="button" onClick={() => setCityFilter('')} className="text-[11px] font-semibold text-[#1A4A8A] hover:underline">Clear</button>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {cityOptions.map(([city, count]) => {
              const active = cityFilter === city;
              return (
                <button
                  key={city} type="button"
                  onClick={() => setCityFilter(active ? '' : city)}
                  className={`px-3 py-1.5 rounded-[8px] border text-[12px] font-semibold transition ${
                    active ? 'bg-[#1A4A8A] border-[#1A4A8A] text-white' : 'bg-white border-[#E5E7EB] text-[#374151] hover:border-[#93C5FD]'
                  }`}
                >
                  {city} <span className={active ? 'text-white/70' : 'text-[#9CA3AF]'}>· {count}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Filter by year(s) of birth — for grouping same-age teams into tournaments */}
      {yobOptions.length > 0 && (
        <div>
          <div className="flex items-center gap-3 mb-2">
            <p className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-[0.9px]">Years of birth</p>
            {yobFilter.length > 0 && (
              <button type="button" onClick={() => setYobFilter([])} className="text-[11px] font-semibold text-[#1A4A8A] hover:underline">Clear</button>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {yobOptions.map((y) => {
              const active = yobFilter.includes(y);
              return (
                <button
                  key={y} type="button"
                  onClick={() => setYobFilter((prev) => active ? prev.filter((v) => v !== y) : [...prev, y])}
                  className={`px-3 py-1.5 rounded-[8px] border text-[12px] font-semibold transition ${
                    active ? 'bg-[#1A4A8A] border-[#1A4A8A] text-white' : 'bg-white border-[#E5E7EB] text-[#374151] hover:border-[#93C5FD]'
                  }`}
                >
                  {y}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* List */}
      {loading ? (
        <p className="text-[#9CA3AF] text-[14px]">Loading leads…</p>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-[12px] border border-[#E5E7EB] p-8 text-center">
          <p className="text-[15px] font-semibold text-[#374151] mb-1">
            {leads.length === 0 ? 'No leads yet' : 'No leads match your filters'}
          </p>
          <p className="text-[13px] text-[#9CA3AF]">
            {leads.length === 0
              ? 'Click "Add lead" to get started.'
              : hiddenScraped > 0
                ? `${hiddenScraped} scraped lead(s) are hidden — tick "Include scraped teams" to work them too.`
                : 'Try clearing the search or filters.'}
          </p>
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((lead) => {
            const contacted = fmtDate(lead.lastContactedAt);
            const latest = normalizeLog(lead.noteLog, lead.notes)[0];
            return (
              <div
                key={lead.id}
                onClick={() => setSelectedId(lead.id)}
                className="bg-white rounded-[12px] border border-[#E5E7EB] p-4 flex flex-col text-left hover:border-[#93C5FD] hover:shadow-sm transition cursor-pointer"
              >
                <div className="flex items-start justify-between gap-2 mb-1">
                  <p className="text-[14px] font-bold text-[#111827] pr-1 flex-1 min-w-0">{lead.clubSchool || lead.contactName || 'Untitled lead'}</p>
                  <StatusBadge status={lead.status} />
                </div>
                {lead.origin === 'portal-customer' && (
                  <div className="flex items-center gap-1.5 mb-1">
                    <span className="px-1.5 py-0.5 bg-[#ECFDF5] text-[#065F46] rounded-full text-[10px] font-semibold uppercase tracking-wide">Client</span>
                    {lead.tourName && <span className="text-[11px] text-[#9CA3AF] truncate">{lead.tourName}</span>}
                  </div>
                )}
                {enableCrm && lead.inCrm && (
                  <div className="flex items-center gap-1.5 mb-1">
                    <span className="px-1.5 py-0.5 bg-[#EFF6FF] text-[#1A4A8A] rounded-full text-[10px] font-semibold uppercase tracking-wide">In CRM</span>
                  </div>
                )}
                {lead.contactName && lead.clubSchool && lead.contactName !== lead.clubSchool && (
                  <p className="text-[12px] text-[#6B7280]">{lead.contactName}</p>
                )}
                {(lead.email || lead.phone) && (
                  <p className="text-[12px] text-[#6B7280] mt-0.5">{[lead.email, lead.phone].filter(Boolean).join(' · ')}</p>
                )}
                {lead.address && <p className="text-[12px] text-[#9CA3AF] mt-0.5">{lead.address}</p>}
                {padel && (lead.playingLevel || lead.partySize || lead.accommodation) && (
                  <div className="flex flex-wrap gap-1 mt-1.5">
                    {lead.playingLevel && (
                      <span title={levelLabel(lead.playingLevel)} className="px-1.5 py-0.5 bg-[#EFF6FF] border border-[#93C5FD] rounded-full text-[10px] font-semibold text-[#1A4A8A]">Level {lead.playingLevel}</span>
                    )}
                    {lead.partySize > 0 && (
                      <span className="px-1.5 py-0.5 bg-[#F3F4F6] border border-[#E5E7EB] rounded-full text-[10px] font-semibold text-[#374151]">{lead.partySize} travelling</span>
                    )}
                    {lead.accommodation && (
                      <span className="px-1.5 py-0.5 bg-[#ECFDF5] border border-[#A7F3D0] rounded-full text-[10px] font-semibold text-[#065F46]">{lead.accommodation}</span>
                    )}
                  </div>
                )}
                {Array.isArray(lead.yearsOfBirth) && lead.yearsOfBirth.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1.5">
                    {lead.yearsOfBirth.map((y) => (
                      <span key={y} className="px-1.5 py-0.5 bg-[#EFF6FF] border border-[#93C5FD] rounded-full text-[10px] font-semibold text-[#1A4A8A]">{y}</span>
                    ))}
                  </div>
                )}
                {showSource && lead.source && (
                  <p className="text-[11px] text-[#9CA3AF] mt-1">Source: <span className="font-medium text-[#6B7280]">{lead.source}</span></p>
                )}
                {latest && <p className="text-[12px] text-[#374151] mt-2 line-clamp-2 whitespace-pre-wrap">{latest.text}</p>}

                <div className="mt-3 pt-3 border-t border-[#F3F4F6] flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-[11px] text-[#9CA3AF] truncate">
                      {lead.assignedToName ? `Assigned: ${lead.assignedToName}` : 'Unassigned'}
                    </p>
                    {contacted && <p className="text-[11px] text-[#9CA3AF]">Contacted: {contacted}</p>}
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); markContacted(lead); }}
                    title="Mark contacted today"
                    className="p-1.5 text-[#9CA3AF] hover:text-[#065F46] transition rounded flex-shrink-0"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {selected && !editing && (
        <LeadDetailModal
          lead={selected}
          staff={staff}
          currentUser={currentUser}
          showSource={showSource}
          padel={padel}
          canManage={canManage}
          enableCrm={enableCrm}
          onPatch={(data) => patchLead(selected.id, data)}
          onEdit={() => { setEditing(selected); setSelectedId(null); }}
          onDelete={() => handleDelete(selected)}
          onCopyToCrm={onCopyToCrm}
          onViewInCrm={onViewInCrm}
          onClose={() => setSelectedId(null)}
        />
      )}

      {editing && (
        <LeadModal
          lead={editing.id ? editing : null}
          staff={staff}
          showSource={showSource}
          padel={padel}
          onClose={() => setEditing(null)}
          onSave={handleSave}
        />
      )}
    </div>
  );
}
